<template>
 <div class="relative flex h-[calc(100vh-57px)] max-w-[1400px] mx-auto overflow-hidden">

  <!-- Left Sidebar (collapsible) -->
  <aside
    class="flex flex-col bg-white border-r border-gray-200 h-full transition-all duration-300 overflow-hidden"
    :class="sidebarOpen ? 'w-[240px] min-w-[240px]' : 'w-0 min-w-0 border-r-0'"
  >

    <!-- Uploaded Files Section -->
    <div class="px-4 pt-4 pb-3 flex-shrink-0" v-if="sessionFiles.length || uploadingFiles.length">
      <div class="flex items-center gap-1.5 mb-2.5">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-gray-400">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
        </svg>
        <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">업로드 파일</span>
      </div>

      <!-- Uploading -->
      <div v-if="uploadingFiles.length > 0" class="space-y-2 mb-3">
        <div v-for="file in uploadingFiles" :key="`uploading-${file.file_id}`" class="rounded-lg bg-gray-50 p-2.5">
          <div class="text-[11px] text-gray-600 mb-1.5 truncate">{{ file.file_name }}</div>
          <div class="w-full bg-gray-200 rounded-full h-1">
            <div
              class="h-1 rounded-full transition-all duration-500"
              :class="{
                'bg-violet-500': file.status === 'processing',
                'bg-emerald-500': file.status === 'completed',
                'bg-red-400': file.status === 'failed',
                'bg-gray-300': file.status === 'pending'
              }"
              :style="{ width: `${file.progress || 0}%` }"
            ></div>
          </div>
          <div class="text-[10px] text-gray-400 mt-1">{{ getStatusText(file.status) }} {{ file.progress || 0 }}%</div>
        </div>
      </div>

      <!-- Files -->
      <div class="space-y-0.5">
        <div v-for="file in sessionFiles" :key="file.file_id">
          <Checkbox
            :label="`${file.file_name} ${file.status ? '(' + file.status + ')' : ''}`"
            :value="file.file_id"
            v-model="selectedFiles"
            class="flex-1 min-w-0 text-xs text-left break-words"
          />
        </div>
      </div>

      <div class="border-t border-gray-100 mt-3" v-if="linkedProject.length"></div>
    </div>

    <!-- Projects Section -->
    <div class="flex-1 min-h-0 overflow-hidden flex flex-col" v-if="linkedProject.length">
      <div class="flex items-center gap-1.5 px-4 pt-3 pb-1">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-violet-400">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
        </svg>
        <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">연결된 프로젝트</span>
        <span class="ml-auto text-[10px] text-violet-500 bg-violet-50 rounded-full px-1.5 py-0.5 font-medium">{{ linkedProject.length }}</span>
      </div>
      <div class="flex-1 min-h-0 overflow-y-auto sidebar-scroll pb-4">
        <AllProjectList
          :projects="linkedProject"
          :sort-option="'name'"
          :filter-text="''"
          :compact="true"
          v-model:parents="checkedParents"
          v-model:children="checkedChildren"
          @update:parents="(val) => checkedParents = val"
          @update:children="(val) => checkedChildren = val"
          @onClick="() => {}"
        />
      </div>
    </div>

    <!-- Empty sidebar state -->
    <div v-if="!linkedProject.length && !sessionFiles.length && !uploadingFiles.length" class="flex-1 flex items-center justify-center px-4">
      <p class="text-xs text-gray-300 text-center">연결된 프로젝트가 없습니다</p>
    </div>
  </aside>

  <!-- Chat Area -->
  <div class="flex-1 min-w-0 flex flex-col">
    <ChatWrapper
      class="flex-1 min-h-0"
      :selectedJobIds="selectedCrawlingJobIds"
      :selectedFileIds="selectedFiles"
      @file-uploaded="loadSessionFiles"
      @toggle-sidebar="sidebarOpen = !sidebarOpen"
    />
  </div>
 </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import Checkbox from '@/components/ui/Checkbox.vue'
import ChatWrapper from '@/components/ChatWrapper.vue'
import AllProjectList from '@/components/AllProjectList.vue'

const store = useStore()
const route = useRoute()
const linkedProject = ref([])
const selectedFiles = ref([])
const sidebarOpen = ref(true)

const checkedParents = ref([])
const checkedChildren = reactive({})

const sessionFiles = ref([]);
const sessionId = computed(() => {
  return route.query.historyId || (store.state.chat.session ? store.state.chat.session.id : null);
});

const uploadingFiles = computed(() => {
  const currentSessionId = sessionId.value;
  if (!currentSessionId) return [];
  return store.getters['file/getUploadingFilesBySession'](currentSessionId) || [];
});

const getLocalStorageKey = () => {
  const sessionId = route.query.historyId || store.state.chat.sessionId || 'default';
  return `chatpub-selected-projects-${sessionId}`;
}

function loadSelectedState() {
  try {
    const key = getLocalStorageKey();
    const savedState = localStorage.getItem(key);
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      checkedParents.value = parsedState.checkedParents || [];
      Object.assign(checkedChildren, parsedState.checkedChildren || {});
    }
  } catch (error) {
    console.error('Failed to load state from localStorage:', error);
  }
}

function saveSelectedState() {
  try {
    const key = getLocalStorageKey();
    const stateToSave = {
      checkedParents: checkedParents.value,
      checkedChildren: { ...checkedChildren }
    };
    localStorage.setItem(key, JSON.stringify(stateToSave));
  } catch (error) {
    console.error('Failed to save state to localStorage:', error);
  }
}

watch(checkedParents, () => saveSelectedState(), { deep: true })
watch(checkedChildren, () => saveSelectedState(), { deep: true })

watch(() => [route.query.historyId, store.state.chat.sessionId], () => {
  if (!route.query.id) loadSelectedState();
}, { immediate: true });

const selectedCrawlingJobIds = computed(() => {
  return Object.values(checkedChildren).reduce((acc, cur) => {
    if (cur && cur.length) acc.push(...cur)
    return acc
  }, [])
})

async function loadSessionFiles() {
  if (!sessionId.value) return;
  try {
    const result = await store.dispatch('file/getSessionFiles', sessionId.value);
    if (result.success) sessionFiles.value = result.files;
  } catch (error) {
    console.error("Failed to load session files:", error);
  }
}

watch(sessionId, (newVal) => { if (newVal) loadSessionFiles(); });

async function getNewProjectData() {
  try {
    await store.dispatch('project/fetchProjectCrawlings')
    const allProjects = store.state.project.projectCrawlings
    const filteredProjects = allProjects.filter((project) =>
      project.crawling_jobs.some((job) =>
        route.query.id.split('|').includes(String(job.id))
      )
    )
    linkedProject.value = filteredProjects.map((proj) => ({
      name: proj.name,
      crawling_jobs: proj.crawling_jobs
        ? proj.crawling_jobs.filter((job) =>
            route.query.id.split('|').includes(String(job.id))
          )
        : [],
      id: proj.id,
      created_at: proj.created_at,
      project_id: proj.id,
      msg_id: null,
    }))

    Object.assign(
      checkedChildren,
      linkedProject.value.reduce((acc, cur) => {
        if (cur?.crawling_jobs?.length) acc[cur.id] = [];
        return acc;
      }, {})
    );

    const selectedJobs = filteredProjects.flatMap((project) =>
      project.crawling_jobs.filter((job) =>
        route.query.id.split('|').includes(String(job.id))
      )
    )
    const projectIds = filteredProjects.map(proj => proj.id)
    loadSelectedState()

    await store.dispatch('chat/initializeChat', {
      selectedJobs,
      projectIds,
      sessionId: null
    })
  } catch (error) {
    console.error(error)
  }
}

async function getPrevProjectData() {
  try {
    await store.dispatch('chat/getSession', +route.query.historyId)
    const session = store.state.chat.session

    await store.dispatch('project/fetchProjectCrawlings')
    const allProjects = store.state.project.projectCrawlings
    const filteredProjects = allProjects.filter(project =>
      session.project_ids.includes(project.id)
    )

    linkedProject.value = filteredProjects.map(proj => ({
      name: proj.name,
      crawling_jobs: proj.crawling_jobs
        ? proj.crawling_jobs.filter(job =>
            session.crawling_job_ids.includes(job.id)
          )
        : [],
      id: proj.id,
      created_at: proj.created_at,
      project_id: proj.id,
      msg_id: session.id
    }))

    Object.assign(
      checkedChildren,
      linkedProject.value.reduce((acc, cur) => {
        if (cur?.crawling_jobs?.length) acc[cur.id] = [];
        return acc;
      }, {})
    );

    const projectIds = session.project_ids
    loadSelectedState()

    await store.dispatch('chat/initializeChat', {
      selectedJobs: linkedProject.value.flatMap(proj => proj.crawling_jobs),
      projectIds,
      sessionId: session.id
    })

    await loadSessionFiles();
  } catch (error) {
    console.error(error)
  }
}

if (route.query.id) getNewProjectData()
if (route.query.historyId) getPrevProjectData()

function getStatusText(status) {
  const statusMap = { 'pending': '대기 중', 'processing': '처리 중', 'completed': '완료', 'failed': '실패' };
  return statusMap[status] || status;
}

watch(uploadingFiles, (newFiles, oldFiles) => {
  const completedFiles = newFiles.filter(file => file.status === 'completed');
  const wasCompleted = oldFiles?.filter(file => file.status === 'completed') || [];
  if (completedFiles.length > wasCompleted.length) {
    setTimeout(() => loadSessionFiles(), 1000);
  }
}, { deep: true });

onUnmounted(() => {
  store.dispatch('file/stopAllFileStatusTracking');
})
</script>

<style scoped>
.sidebar-scroll::-webkit-scrollbar { width: 4px; }
.sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.08); border-radius: 4px; }
.sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
</style>
